/**
 * Contact Form Handler
 * Handles form submission to HubSpot API (public submission - no authentication required)
 */

import { getHubSpotFormUrl } from '../utils/hubspotConfig.js';

export class ContactForm {
  constructor(formElement) {
    if (!formElement) {
      console.error('Contact form element not found');
      return null;
    }

    this.form = formElement;
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.originalButtonText = this.submitButton ? this.submitButton.textContent : 'Send message';
    this.isSubmitting = false;

    this.init();
  }

  /**
   * Initialize form handler
   */
  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    console.log('Contact form initialized with HubSpot integration');
  }

  /**
   * Handle form submission
   * @param {Event} event - Form submit event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) {
      return;
    }

    // Remove any existing feedback messages
    this.removeMessage();

    // Validate form
    if (!this.validateForm()) {
      this.showMessage('Please fill in all required fields.', 'error');
      return;
    }

    // Get form data
    const formData = this.getFormData();

    // Submit to HubSpot
    await this.submitToHubSpot(formData);
  }

  /**
   * Validate form fields
   * @returns {boolean} True if form is valid
   */
  validateForm() {
    const firstName = this.form.querySelector('#firstName');
    const lastName = this.form.querySelector('#lastName');
    const email = this.form.querySelector('#email');
    const phone = this.form.querySelector('#phone');
    const message = this.form.querySelector('#message');

    return (
      firstName?.value.trim() &&
      lastName?.value.trim() &&
      email?.value.trim() &&
      this.isValidEmail(email.value.trim()) &&
      phone?.value.trim() &&
      message?.value.trim()
    );
  }

  /**
   * Validate email format
   * @param {string} email - Email address to validate
   * @returns {boolean} True if email is valid
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get form data formatted for HubSpot
   * @returns {object} Formatted form data
   */
  getFormData() {
    const firstName = this.form.querySelector('#firstName').value.trim();
    const lastName = this.form.querySelector('#lastName').value.trim();
    const email = this.form.querySelector('#email').value.trim();
    const message = this.form.querySelector('#message').value.trim();
    const phone = this.form.querySelector('#phone').value.trim();
    const countryCode = this.form.querySelector('.country-select')?.value || 'US';

    return {
      firstName,
      lastName,
      email,
      message,
      phone: phone ? `${countryCode} ${phone}` : '',
      countryCode
    };
  }

  /**
   * Submit form data to HubSpot
   * @param {object} formData - Form data to submit
   */
  async submitToHubSpot(formData) {
    this.setLoadingState(true);

    try {
      const hubspotUrl = getHubSpotFormUrl();

      // Format data for HubSpot API
      const hubspotData = {
        fields: [
          {
            objectTypeId: '0-1',
            name: 'firstname',
            value: formData.firstName
          },
          {
            objectTypeId: '0-1',
            name: 'lastname',
            value: formData.lastName
          },
          {
            objectTypeId: '0-1',
            name: 'email',
            value: formData.email
          },
          {
            objectTypeId: '0-1',
            name: 'phone',
            value: formData.phone
          },
          {
            objectTypeId: '0-1',
            name: 'message',
            value: formData.message
          }
        ],
        context: {
          pageUri: window.location.href,
          pageName: document.title
        }
      };

      // Submit to HubSpot (public form submission - no authentication required)
      const response = await fetch(hubspotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(hubspotData)
      });

      if (response.ok) {
        this.handleSuccess();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('HubSpot submission error:', errorData);
        this.handleError(errorData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      this.handleError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Handle successful form submission
   */
  handleSuccess() {
    this.showMessage(
      'Thank you for your message! We\'ll get back to you soon.',
      'success'
    );
    this.form.reset();
  }

  /**
   * Handle form submission error
   * @param {object} error - Error object
   */
  handleError(error) {
    const errorMessage = error.message ||
      'There was an error sending your message. Please try again or contact us directly at info@brainstork.com';
    this.showMessage(errorMessage, 'error');
  }

  /**
   * Set loading state for form submission
   * @param {boolean} isLoading - Whether form is loading
   */
  setLoadingState(isLoading) {
    this.isSubmitting = isLoading;

    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Sending...';
      this.submitButton.classList.add('loading');
    } else {
      this.submitButton.disabled = false;
      this.submitButton.textContent = this.originalButtonText;
      this.submitButton.classList.remove('loading');
    }
  }

  /**
   * Show message to user
   * @param {string} message - Message text
   * @param {string} type - Message type ('success' or 'error')
   */
  showMessage(message, type) {
    this.removeMessage();

    const messageElement = document.createElement('div');
    messageElement.className = `form-message form-message--${type}`;
    messageElement.textContent = message;
    messageElement.setAttribute('role', type === 'error' ? 'alert' : 'status');

    // Insert message before submit button
    if (this.submitButton) {
      this.submitButton.parentNode.insertBefore(messageElement, this.submitButton);
    } else {
      this.form.appendChild(messageElement);
    }

    // Auto-remove success messages after 5 seconds
    if (type === 'success') {
      setTimeout(() => {
        this.removeMessage();
      }, 5000);
    }
  }

  /**
   * Remove existing message
   */
  removeMessage() {
    const existingMessage = this.form.querySelector('.form-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  /**
   * Destroy form handler
   */
  destroy() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit.bind(this));
    }
  }
}

/**
 * Initialize contact form
 * @param {string} selector - Form selector
 * @returns {ContactForm|null} ContactForm instance or null
 */
export function initContactForm(selector = '.contact-form') {
  const formElement = document.querySelector(selector);
  if (!formElement) {
    console.warn(`Contact form not found with selector: ${selector}`);
    return null;
  }
  return new ContactForm(formElement);
}
